import { useState, useMemo, useEffect } from 'react';
import { StackedBarChartVizData } from './EnhancedStackedBarChart.types';

export type StackedBarChartRow = {
    category: string;
    series: string;
    value: number;
};

/** Check if z is flat [n,n,n] vs nested [[n,n,n]] */
function isFlatZ(z: unknown): z is number[] {
    return Array.isArray(z) && z.length > 0 && typeof z[0] === 'number';
}

/** Get series labels from zAxisTitle (supports ["A","B"] or [["A","B"]]) */
function getZLabels(zAxisTitle: unknown): string[] {
    if (!zAxisTitle || !Array.isArray(zAxisTitle)) return [];
    const first = zAxisTitle[0];
    return Array.isArray(first) ? first.map(String) : zAxisTitle.map(String);
}

/**
 * Normalize vizData to internal format (x, z as number[][], zAxisTitle as string[][]).
 * Supports: flat z [500,120,45], nested z [[500,120,45]], and legacy y format.
 */
function normalizeStackedBarVizData(vizData: any): StackedBarChartVizData | null {
    if (!vizData?.data || !Array.isArray(vizData.data)) return null;

    const hasZ = vizData.data.some((p: any) => p.z != null && Array.isArray(p.z) && p.z.length > 0);
    const hasY = vizData.data.some((p: any) => p.y != null && Array.isArray(p.y));

    const zLabels = getZLabels(vizData.zAxisTitle);
    if (hasZ && zLabels.length > 0) {
        const normalizedData = vizData.data.map((p: any) => {
            const xArr = Array.isArray(p.x) ? p.x : [p.x];
            const zRaw = p.z ?? [];
            const zValues: number[] = isFlatZ(zRaw) ? zRaw : Array.isArray(zRaw?.[0]) ? zRaw[0] : [];
            return { x: xArr.map(String), z: [zValues] };
        });
        return {
            ...vizData,
            zAxisTitle: [zLabels],
            data: normalizedData,
        };
    }

    // Legacy: y format with yAxisTitle[0] = series labels
    if (hasY) {
        const yAxisTitle = Array.isArray(vizData.yAxisTitle) ? vizData.yAxisTitle : [];
        const seriesLabels = Array.isArray(yAxisTitle[0]) ? yAxisTitle[0] : yAxisTitle;
        const normalizedData: Array<{ x: string[]; z: number[][] }> = [];
        for (const point of vizData.data) {
            const xArr = Array.isArray(point.x) ? point.x : [point.x];
            const yArr = point.y ?? [];
            for (let i = 0; i < xArr.length; i++) {
                const zValues = Array.isArray(yArr[i]) ? yArr[i] : Array.isArray(yArr[0]) ? yArr[0] : [];
                normalizedData.push({ x: [String(xArr[i])], z: [zValues] });
            }
        }
        return {
            ...vizData,
            zAxisTitle: [seriesLabels.map((l: string) => String(l))],
            yAxisTitle: Array.isArray(yAxisTitle[1]) ? yAxisTitle[1] : ['Value'],
            data: normalizedData,
        };
    }

    return null;
}

/**
 * Hook to manage Z filter for stacked bar chart.
 * Only Z filter is present - to filter which stacked series (factors) to display.
 */
export function useStackedBarChartFilters(vizData: StackedBarChartVizData | undefined) {
    const [selectedZ, setSelectedZ] = useState<string[]>([]);

    const normalizedVizData = useMemo(() => normalizeStackedBarVizData(vizData as any), [vizData]);

    const zOptions = useMemo(() => {
        if (!normalizedVizData?.zAxisTitle?.[0]) return [];
        const first = normalizedVizData.zAxisTitle[0];
        const arr = Array.isArray(first) ? first : [first];
        return arr.map((label: string) => String(label));
    }, [normalizedVizData]);

    // Default to all z series selected when vizData loads
    useEffect(() => {
        if (!normalizedVizData?.zAxisTitle?.[0]) return;
        const first = normalizedVizData.zAxisTitle[0];
        const arr = Array.isArray(first) ? first : [first];
        if (arr.length === 0) return;
        const allZ = arr.map((l: string) => String(l));
        setSelectedZ((prev) => {
            if (prev.length === 0) return allZ;
            return prev;
        });
    }, [normalizedVizData?.zAxisTitle]);

    const updateZFilter = (value: string[]) => {
        setSelectedZ(Array.isArray(value) ? value : [value]);
    };

    // Transform filtered data to chart format: { category, series, value }[]
    const chartData = useMemo((): StackedBarChartRow[] => {
        if (!normalizedVizData?.data || !Array.isArray(normalizedVizData.data)) return [];
        if (selectedZ.length === 0) return [];

        const zLabels = normalizedVizData.zAxisTitle?.[0] ?? [];
        const rows: StackedBarChartRow[] = [];

        for (const point of normalizedVizData.data) {
            const category = Array.isArray(point.x) ? String(point.x[0] ?? '') : String(point.x ?? '');
            const zValues = Array.isArray(point.z?.[0]) ? point.z[0] : [];

            selectedZ.forEach((seriesLabel) => {
                const idx = zLabels.indexOf(seriesLabel);
                if (idx >= 0) {
                    const value = Number(zValues[idx]);
                    if (Number.isFinite(value)) {
                        rows.push({ category, series: seriesLabel, value });
                    }
                }
            });
        }

        return rows;
    }, [normalizedVizData, selectedZ]);

    return {
        selectedZ,
        zOptions,
        updateZFilter,
        chartData,
        normalizedVizData,
    };
}
